import React, { useState, useEffect, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Button from "react-bootstrap/Button";
import Offcanvas from "react-bootstrap/Offcanvas";
import FormBootstrap from "react-bootstrap/Form";
import { useSelector, useDispatch } from "react-redux";
import {
  formatDateToISO,
  toFrenchTimeUTC,
  parseDateUTC,
  InverseparseDateFromDDMMYY,
  parseDateTime,
  datewithTiret,
} from "../../utils/utils";
import {
  cancelRdvManual,
  fetchCalendarData,
} from "../../store/slices/rendezVousPrestaSlice";
import { toast } from "react-toastify";
import LocationAutocomplete from "../LocationAutocomplete";
import { useLoadScript } from "@react-google-maps/api";
import dayjs from "dayjs";

const validationSchema = Yup.object().shape({
  title: Yup.string().required("Champ obligatoire"),

  date: Yup.string()
    .required("Champ requis")
    .test(
      "is-today-or-future",
      "La date ne peut pas être dans le passé",
      function (value) {
        if (!value) return false;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const selected = InverseparseDateFromDDMMYY(value);
        selected.setHours(0, 0, 0, 0);

        return selected >= today;
      }
    ),

  start_time: Yup.string()
    .required("Champ obligatoire")
    .test(
      "start-time-not-in-past",
      "L'heure de début ne peut pas être dans le passé",
      function (value) {
        const { date } = this.parent;
        if (!date || !value) return true;

        const today = new Date();

        const selectedDate = InverseparseDateFromDDMMYY(date);

        if (selectedDate.toDateString() === today.toDateString()) {
          const [hours, minutes] = value.split(":");
          const selectedStartTime = new Date(selectedDate);
          selectedStartTime.setHours(Number(hours), Number(minutes), 0, 0);

          return selectedStartTime > today;
        }

        return true;
      }
    ),

  end_time: Yup.string()
    .required("Champ obligatoire")
    .test(
      "is-after-start",
      "L'heure de fin doit être supérieure à l'heure de début",
      function (value) {
        const { start_time } = this.parent;
        if (!start_time || !value) return true;
        return value > start_time;
      }
    ),

  equipe_id: Yup.string().required("Champ obligatoire"),
  indisponible: Yup.boolean(),
  last_name: Yup.string().when("indisponible", {
    is: false,
    then: (schema) => schema.required("Champ obligatoire"),
    otherwise: (schema) => schema.notRequired(),
  }),
  first_name: Yup.string().when("indisponible", {
    is: false,
    then: (schema) => schema.required("Champ obligatoire"),
    otherwise: (schema) => schema.notRequired(),
  }),
  lieu_intervention: Yup.string().when("indisponible", {
    is: false,
    then: (schema) => schema.required("Champ obligatoire"),
    otherwise: (schema) => schema.notRequired(),
  }),
  meeting_link: Yup.string().when(["indisponible", "lieu_intervention"], {
    is: (indisponible, lieu_intervention) =>
      !indisponible && lieu_intervention === "enligne",
    then: (schema) => schema.required("Lien requis").url("Lien invalide"),
    otherwise: (schema) => schema.notRequired(),
  }),
  adresse_domicile: Yup.string().when(["indisponible", "lieu_intervention"], {
    is: (indisponible, lieu_intervention) =>
      !indisponible && lieu_intervention === "onsite",
    then: (schema) => schema.required("Champ obligatoire"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

function EditRdvManual({ show, handleClose, event: rdv, ...props }) {
  const [indisponible, setIndisponible] = useState(false);
  const { allEtablissementEquipes } = useSelector((state) => state.rdvPresta);
  const libraries = ["places"];
  const [lieuSelectionne, setLieuSelectionne] = useState(
    rdv?.lieu_intervention || ""
  );
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });
  console.log("event", rdv);
  const formikRef = useRef(null);
  const dispatch = useDispatch();

  const handleLocationSelect = (
    place,
    setFieldValue,
    setFieldTouched,
    fieldName
  ) => {
    const address = place?.description || "";
    setFieldValue(fieldName, address);
    setFieldTouched(fieldName, true);
  };

  console.log(rdv?.start_time)
  console.log(rdv?.end_time)

  const initialValues = {
    title: rdv?.title || "",
    date: rdv?.start_time ? dayjs(rdv?.start_time).format("DD/MM/YY") : "",
    start_time: toFrenchTimeUTC(parseDateUTC(rdv?.start_time)),
    end_time: toFrenchTimeUTC(parseDateUTC(rdv?.end_time)),
    equipe_id: rdv?.equipe_id || "",
    indisponible: rdv?.type === "unavailable",
    last_name: rdv?.manual_user_detail?.last_name || "",
    first_name: rdv?.manual_user_detail?.first_name || "",
    email: rdv?.manual_user_detail?.email || "",
    phone: rdv?.manual_user_detail?.phone || "",
    lieu_intervention: rdv?.lieu_intervention || "",
    adresse_domicile: rdv?.adresse_domicile || "",
    meeting_link: rdv?.meeting_link || "",
  };

  useEffect(() => {
    setIndisponible(initialValues.indisponible);
  }, [show]);

  useEffect(() => {
    setLieuSelectionne(rdv?.lieu_intervention || "");
  }, [rdv?.lieu_intervention]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const formattedDate = datewithTiret(values?.date);

      const start_time = `${formattedDate} ${values?.start_time}:00`;
      const end_time = `${formattedDate} ${values?.end_time}:00`;

      const payload = {
        ...values,
        start_time,
        end_time,
        id: rdv?.id,
      };
      delete payload.date;

      await dispatch(cancelRdvManual(payload)).unwrap();

      toast.success(
        values.indisponible
          ? "L'indisponibilité modifiée avec succès"
          : "Rendez-vous modifié avec succès"
      );

      handleClose();
    } catch (error) {
      toast.error(
        error.message || "Une erreur est survenue lors de la mise à jour."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Offcanvas
      show={show}
      placement="end"
      onHide={handleClose}
      {...props}
      className="agenda"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title className="agendaTiltle">
          Modifier le rendez-vous
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({
            values,
            setFieldValue,
            setFieldTouched,
            touched,
            errors,
            handleBlur,
          }) => (
            <Form className="m-24">
              <FormBootstrap.Group className="mb-3">
                <FormBootstrap.Label className="legend">
                  Titre du rendez-vous*
                </FormBootstrap.Label>
                <Field name="title" className="form-control input" />
                <ErrorMessage
                  name="title"
                  className="text-danger"
                  component="div"
                />
              </FormBootstrap.Group>
              <div className="d-flex">
                <FormBootstrap.Group className="mb-3 w-30 m-4">
                  <FormBootstrap.Label className="legend">
                    Date*
                  </FormBootstrap.Label>
                  <Field name="date" className="form-control input" />
                  <ErrorMessage
                    name="date"
                    className="text-danger"
                    component="div"
                  />
                </FormBootstrap.Group>
                <FormBootstrap.Group className="mb-3 w-30 m-4">
                  <FormBootstrap.Label className="legend">
                    De*
                  </FormBootstrap.Label>
                  <Field
                    name="start_time"
                    className="form-control input"
                    type="time"
                  />
                  <ErrorMessage
                    name="start_time"
                    className="text-danger"
                    component="div"
                  />
                </FormBootstrap.Group>
                <FormBootstrap.Group className="mb-3 w-30 m-4">
                  <FormBootstrap.Label className="legend">
                    À*
                  </FormBootstrap.Label>
                  <Field
                    name="end_time"
                    className="form-control input"
                    type="time"
                  />
                  <ErrorMessage
                    name="end_time"
                    className="text-danger"
                    component="div"
                  />
                </FormBootstrap.Group>
              </div>
              <FormBootstrap.Group className="mb-3">
                <FormBootstrap.Label className="legend">
                  Salarié
                </FormBootstrap.Label>
                <Field
                  as="select"
                  name="equipe_id"
                  className="form-control input"
                >
                  <option value="">Choisir un salarié</option>
                  {allEtablissementEquipes?.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.name}
                    </option>
                  ))}
                </Field>
                <ErrorMessage
                  name="equipe_id"
                  className="text-danger"
                  component="div"
                />
              </FormBootstrap.Group>
              <FormBootstrap.Group className="mb-3">
                <FormBootstrap.Check
                  type="checkbox"
                  label="Non disponible sur ces horaires"
                  className="input legend"
                  checked={values.indisponible}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFieldValue("indisponible", checked);
                    setIndisponible(checked);
                  }}
                />
              </FormBootstrap.Group>
              {!values.indisponible && (
                <>
                  <FormBootstrap.Group className="mb-3">
                    <FormBootstrap.Label className="legend">
                      Nom du client
                    </FormBootstrap.Label>
                    <Field name="last_name" className="form-control input" />
                    <ErrorMessage
                      name="last_name"
                      className="text-danger"
                      component="div"
                    />
                  </FormBootstrap.Group>
                  <FormBootstrap.Group className="mb-3">
                    <FormBootstrap.Label className="legend">
                      Prénom du client
                    </FormBootstrap.Label>
                    <Field name="first_name" className="form-control input" />
                    <ErrorMessage
                      name="first_name"
                      className="text-danger"
                      component="div"
                    />
                  </FormBootstrap.Group>
                  <FormBootstrap.Group className="mb-3">
                    <FormBootstrap.Label className="legend">
                      Email du client
                    </FormBootstrap.Label>
                    <Field
                      name="email"
                      type="email"
                      className="form-control input"
                    />
                    <ErrorMessage
                      name="email"
                      className="text-danger"
                      component="div"
                    />
                  </FormBootstrap.Group>
                  <FormBootstrap.Group className="mb-3">
                    <FormBootstrap.Label className="legend">
                      Téléphone du client
                    </FormBootstrap.Label>
                    <Field name="phone" className="form-control input" />
                    <ErrorMessage
                      name="phone"
                      className="text-danger"
                      component="div"
                    />
                  </FormBootstrap.Group>
                  <FormBootstrap.Group className="mb-3">
                    <FormBootstrap.Label className="legend">
                      Lieu d'intervention
                    </FormBootstrap.Label>
                    <Field
                      as="select"
                      name="lieu_intervention"
                      className="form-control input"
                      value={values.lieu_intervention}
                      onChange={(e) => {
                        const selected = e.target.value;
                        setLieuSelectionne(selected);
                        setFieldValue("lieu_intervention", selected);
                      }}
                    >
                      <option value="" disabled selected>
                        Sélectionnez un lieu d'intervention
                      </option>
                      <option value="chezpro">Chez le professionnel</option>
                      <option value="onsite">À domicile</option>
                      <option value="enligne">En ligne</option>
                    </Field>
                    <ErrorMessage
                      name="lieu_intervention"
                      className="text-danger"
                      component="div"
                    />
                  </FormBootstrap.Group>
                  {lieuSelectionne === "enligne" && (
                    <FormBootstrap.Group className="mb-3">
                      <FormBootstrap.Label className="legend">
                        Lien de la visioconférence*
                      </FormBootstrap.Label>
                      <Field
                        as={FormBootstrap.Control}
                        type="url"
                        name="meeting_link"
                        className="form-control input"
                        isInvalid={
                          touched.meeting_link && !!errors.meeting_link
                        }
                      />
                      <FormBootstrap.Control.Feedback type="invalid">
                        {errors.meeting_link}
                      </FormBootstrap.Control.Feedback>
                    </FormBootstrap.Group>
                  )}
                  {lieuSelectionne === "onsite" && (
                    <div className="input-wrapper form-group mt-16">
                      <label className="form-label">Adresse du client*</label>
                      <div className="relative">
                        <LocationAutocomplete
                          onLocationSelect={(place) =>
                            handleLocationSelect(
                              place,
                              setFieldValue,
                              setFieldTouched,
                              "adresse_domicile"
                            )
                          }
                          isGoogleLoaded={isLoaded}
                          value={values.adresse_domicile}
                        />
                      </div>
                      <ErrorMessage
                        name="adresse_domicile"
                        className="text-danger"
                        component="div"
                      />
                    </div>
                  )}
                </>
              )}
            </Form>
          )}
        </Formik>
      </Offcanvas.Body>
      <div className="offcanvas-footer mt-28">
        <div className="container plr24">
          <div className="mt-4 w-30">
            <Button
              onClick={() => {
                if (formikRef.current) {
                  formikRef?.current?.submitForm();
                }
              }}
              type="submit"
              variant="primary"
              className="twyny-cta-primary padding-submit width-100"
            >
              Modifier le rendez-vous
            </Button>
          </div>
          <div className="mt-4 w-30 mt-8">
            <Button
              variant="secondary"
              onClick={handleClose}
              className="twyny-cta-secondary padding-submit width-100"
            >
              Annuler
            </Button>
          </div>
        </div>
      </div>
    </Offcanvas>
  );
}

export default EditRdvManual;
